import React from 'react';
import { useParams, Link, useLocation } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Play, Pause, Send, Clock, Wrench, Plus, X, Pencil, Download } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { JobCardChecklist, type JobCardChecklistHandle } from '@/components/shared/job-card-checklist';
import { JobCardDocuments } from './job-card-documents';
import { jobCardStatusLabel } from './configs';
import { useUserActions } from '@/hooks/use-user-actions';
import { useConfirm } from '@/hooks/use-confirm';
import { useAuthStore } from '@/stores/auth-store';
import { fmtDate, fmtDateTime, toDateInputValue } from '@/lib/format-date';
import { NzTimeInput } from '@/components/shared/nz-datetime-input';
import { downloadJobCardServicePdf } from '@/lib/detail-pdf';

interface JobCard {
  id: string;
  jobSheetNumber: string;
  status: string;
  workDescription: string | null;
  reviewNotes: string | null;
  totalTimeLogged: number | null;
  serviceHistory?: { id: string } | string | null;
  startDate?: string | null;
  asset?: { id: string; fleetNumber?: string };
  /** Populated on the detail read; named in the vendor-mode banner. */
  vendor?: { id: string; name?: string } | null;
  servicedWith?: string | null;
  servicePlan?: { id: string; name?: string };
  servicePlanSchedule?: { id: string; name?: string };
  assignedTo?: Array<{ id: string; fullName?: string }>;
  // Per-tech submission rows. Each assigned tech submits independently;
  // the card only flips to 'completed' once every assignee has a row
  // here. The `employee` field comes from the server as a bare id
  // string when not populated.
  submissions?: Array<{ employee: string | { id: string }; submittedAt: string }>;
}

interface ServiceHistory {
  id: string;
  odometer: number | null;
  engineHours: number | null;
  hubo: number | null;
  descriptionOfWork: string | null;
  faultDescriptions: string | null;
}

interface TimeLog {
  id: string;
  startTime: string | null;
  endTime: string | null;
  hours: number | null;
  employee?: { id: string; fullName?: string };
}

const STATUS_BADGE: Record<string, 'success' | 'secondary' | 'destructive' | 'default'> = {
  active:    'default',
  completed: 'secondary',
  approved:  'success',
  voided:    'destructive',
};

export function JobCardServicePage() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from ?? '/fleet/job-cards';
  // "Log Past Service" mode — entered via the rowAction link
  // `/fleet/job-cards/:id/service?past=1`. Replaces the live Start/Pause
  // controls with an explicit date/start/end window that creates a
  // closed TimeLog spanning the chosen interval.
  const pastMode = new URLSearchParams(location.search).get('past') === '1';
  const vendorParam = new URLSearchParams(location.search).get('vendor') === '1';
  // Friendly label derived from the originating route. Matches the path of
  // the report/page the user came from so the breadcrumb stays meaningful
  // (e.g. "Back to Log Services" when launched via Edit on services_page).
  const backLabel = (() => {
    if (backTo.startsWith('/job-cards/dashboard')) return 'Back to Workshop';
    if (backTo.startsWith('/servicing/history') || backTo.startsWith('/servicing/dashboard')) return 'Back to Log Services';
    if (backTo.startsWith('/dashboard')) return 'Back to Dashboard';
    return 'Back to Job Cards';
  })();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { actions: granted } = useUserActions();
  const [reviewNotesDraft, setReviewNotesDraft] = React.useState('');
  // Imperative handle on the Service Details form — lets the Submit click
  // flush any in-flight draft edits before the JobCard transitions to
  // 'completed' (and the form goes readOnly).
  const serviceFormRef = React.useRef<ServiceDetailsFormHandle | null>(null);
  // Imperative handle on the checklist — used in past mode to flush deferred
  // answer / comment edits to the server alongside the Service Details flush
  // when the tech clicks Submit. Live mode doesn't need this (the checklist
  // autosaves on every commit), so the ref stays null there.
  const checklistRef = React.useRef<JobCardChecklistHandle | null>(null);
  // Subscribe to auth here at the top — hooks must run unconditionally,
  // so the value is captured before any of the early-return guards below.
  const currentUserId = useAuthStore((s) => s.employee?.id ?? null);
  // View-mode editing toggle. The page lands read-only for completed /
  // approved / voided cards; a reviewer with edit rights clicks "Edit"
  // in the top-right to unlock Service Details, Checklist and Documents.
  // Save commits any deferred draft via the form's imperative handle and
  // returns to read-only; Cancel discards the in-memory draft (by
  // re-syncing from the server) and returns to read-only.
  const [isEditing, setIsEditing] = React.useState(false);
  // PDF export — busy flag prevents double-download while checklist answers
  // are being fetched. Mirrors the spinner approach used on the report
  // DetailDialog's PDF button.
  const [pdfBusy, setPdfBusy] = React.useState(false);

  const { data: jc, isLoading } = useQuery({
    queryKey: ['/fleet/job-cards', id],
    queryFn: async () => {
      const r = await api<JobCard>(`/fleet/job-cards/${id}`);
      return r.data!;
    },
    enabled: !!id,
  });
  // "Log Vendor Service" mode. An external vendor did the work, so there is
  // no Atlas technician to time: no Start/Pause, no TimeLog, no window rows.
  // The operator records the vendor's details, saves, then submits straight
  // into the review queue. Derived from the card itself as well as `?vendor=1`
  // — Log Services and the Workshop dashboard link here without the param,
  // which used to drop vendor cards into the technician flow (Start Service,
  // no Save button).
  const vendorMode = !pastMode && (vendorParam || jc?.servicedWith === 'Vendor');
  // Both deferred-entry modes share the same shape: scaffold the record up
  // front, hold everything locally, flush on a single Submit.
  const deferredMode = pastMode || vendorMode;

  const { data: timeLogs = [] } = useQuery({
    queryKey: ['time-logs', 'jobCard', id],
    queryFn: async () => {
      const r = await api<TimeLog[]>(`/time-logs?jobCard=${id}&limit=200`);
      return r.data || [];
    },
    enabled: !!id,
  });

  // Past-mode-only pre-submit gate on the checklist. Lifted into the parent
  // (instead of inside <JobCardChecklist/>) so the Submit handler can refuse
  // to fire when items are still unanswered. The live flow stays unchanged —
  // the same shared query key dedupes, so this adds no extra network hit.
  // Items with answer === null are treated as unanswered; 'OK' / 'Not OK' /
  // 'N/A' all count as answered (matches the answer-icon logic).
  const { data: checklistRows = [] } = useQuery<Array<{ id: string; answer: string | null }>>({
    queryKey: ['service-history-checklist-answers', 'jobCard', id],
    queryFn: async () => {
      const r = await api<Array<{ id: string; answer: string | null }>>(`/service-history-checklist-answers?jobCard=${id}&limit=200`);
      return r.data || [];
    },
    enabled: !!id && pastMode,
  });
  const checklistUnanswered = pastMode
    ? checklistRows.filter((row) => !row.answer).length
    : 0;

  const shId = jc?.serviceHistory && typeof jc.serviceHistory === 'object'
    ? jc.serviceHistory.id
    : (jc?.serviceHistory as string | null) || null;

  const { data: sh } = useQuery({
    queryKey: ['/servicing/history', shId],
    queryFn: async () => {
      const r = await api<ServiceHistory>(`/servicing/history/${shId}`);
      return r.data!;
    },
    enabled: !!shId,
  });

  React.useEffect(() => { if (jc) setReviewNotesDraft(jc.reviewNotes ?? ''); }, [jc?.reviewNotes]);

  // Meter-validation context — drives the inline hint on the Service Details
  // form. Mirror of the server-side guard in /job-cards/:id/submit so the
  // technician sees the problem before clicking Submit (server still has
  // the authoritative reject). Asset.trackUsageAs decides which meter to
  // require; latest prior ServiceHistory.{odometer|hubo|engineHours} sets
  // the minimum acceptable value.
  const assetId = jc?.asset?.id ?? null;
  const { data: assetMeta } = useQuery({
    queryKey: ['/fleet/assets', assetId, 'trackUsageAs'],
    queryFn: async () => (await api<{ trackUsageAs?: string | null }>(`/fleet/assets/${assetId}`)).data ?? null,
    enabled: !!assetId,
  });
  const trackUsageAs = assetMeta?.trackUsageAs ?? null;
  const requiredMeter: 'odometer' | 'hubo' | 'engineHours' | null =
    trackUsageAs === 'Odometer'  ? 'odometer'    :
    trackUsageAs === 'Hubometer' ? 'hubo'        :
    trackUsageAs === 'Hours'     ? 'engineHours' : null;

  // Fetch a small slice of recent SH for this asset so we can pick the most
  // recent prior reading that's not this job's own SH (the technician's
  // in-flight row will appear at the top with jobDate=today).
  const { data: priorReadings = [] } = useQuery<ServiceHistory[]>({
    queryKey: ['service-history', 'prior', assetId, shId],
    queryFn: async () => {
      const r = await api<ServiceHistory[]>(
        `/servicing/history?asset=${encodeURIComponent(assetId!)}&limit=10&sort=jobDate&order=desc`,
      );
      return (r.data ?? []).filter((row) => row.id !== shId);
    },
    enabled: !!assetId && !!requiredMeter,
  });
  const priorReading: number | null = React.useMemo(() => {
    if (!requiredMeter) return null;
    for (const row of priorReadings) {
      const v = (row as any)[requiredMeter];
      if (v != null) return Number(v);
    }
    return null;
  }, [priorReadings, requiredMeter]);

  const updateServiceHistory = useMutation({
    mutationFn: async (patch: Partial<ServiceHistory>) => {
      if (!shId) throw new Error('Service record not yet created — start the service first.');
      const r = await api(`/servicing/history/${shId}`, { method: 'PUT', body: JSON.stringify(patch) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/servicing/history', shId] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Past-service form state (only used when `pastMode` is on). The
  // operator can add multiple { date, startTime, endTime } rows — all
  // belonging to a single submission (one tech, one job card,
  // finalised in one click). Each row becomes its own closed TimeLog
  // server-side. We keep the rows as plain strings (not Date objects)
  // so we can render `<Input type="date">`/`type="time"` directly
  // against state.
  type PastWindowRow = { date: string; start: string; end: string };
  const emptyRow: PastWindowRow = { date: '', start: '', end: '' };
  const [pastRows, setPastRows] = React.useState<PastWindowRow[]>([{ ...emptyRow }]);
  const [pastError, setPastError] = React.useState<string | null>(null);
  const [vendorError, setVendorError] = React.useState<string | null>(null);

  function updatePastRow(idx: number, patch: Partial<PastWindowRow>) {
    setPastRows((rows) => rows.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      // If the new start now equals/exceeds the existing end, clear end
      // so the user re-picks a valid one. Same UX we had on the
      // single-row form.
      if (patch.start && next.end && patch.start >= next.end) next.end = '';
      return next;
    }));
    setPastError(null);
  }
  function addPastRow() {
    setPastRows((rows) => [...rows, { ...emptyRow }]);
    setPastError(null);
  }
  function removePastRow(idx: number) {
    setPastRows((rows) => rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx));
    setPastError(null);
  }

  // One-shot init: in past mode, create the ServiceHistory + checklist
  // scaffolding so the Service Details and Checklist editors below can
  // load. Doesn't open a TimeLog — that's deferred until the single
  // /submit-past call below.
  const initPast = useMutation({
    mutationFn: async () => {
      const r = await api(`/fleet/job-cards/${id}/init-past`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['service-history-checklist-answers', 'jobCard', id] });
    },
    // Surface init failures so the tech isn't stuck staring at a
    // "Preparing…" placeholder. Without this the Service Details /
    // Checklist sections would just never appear after a 4xx/5xx.
    onError: (e: any) => toast('Could not prepare past service', e?.message, 'error'),
  });
  // Trigger init once per page load whenever past mode opens on an active
  // card. Runs even when a ServiceHistory already exists — the server
  // call is idempotent (ensureServiceHistory short-circuits, and
  // seedChecklistAnswers re-attempts whenever the answers collection is
  // still empty). This recovers the case where a prior /start created the
  // SH back when the schedule had no checklist items, and items were
  // added to the schedule after the fact.
  // Vendor mode needs exactly the same scaffolding, and `init-past` is
  // already "create the record without opening a TimeLog" — so it is reused
  // rather than duplicated under a second name.
  const initPastFired = React.useRef(false);
  React.useEffect(() => {
    if (!deferredMode) return;
    if (!jc) return;
    if (jc.status !== 'active') return;
    if (initPastFired.current) return;
    // Short-circuit when the ServiceHistory already exists. Without
    // this guard every fresh mount of past mode (e.g., revisiting the
    // page after a quick detour through Start Service) re-fires the
    // mutation; while it's in flight `initPast.isPending` disables the
    // inputs, producing the "sometimes the form is disabled, refresh
    // fixes it" symptom. The server endpoint is still idempotent — we
    // just don't need to hit it when there's nothing to do.
    initPastFired.current = true;
    if (jc.serviceHistory) return;
    initPast.mutate();
  }, [deferredMode, jc?.id, jc?.status, jc?.serviceHistory]);

  // Vendor Save — commits the deferred Service Details draft without changing
  // status, so the operator can record the vendor's details and come back
  // before sending for approval. Without it, Submit/Approve were the only
  // writes and leaving the page discarded everything typed.
  const saveVendor = useMutation({
    mutationFn: async () => {
      const saved = await serviceFormRef.current?.flush();
      return !!saved;
    },
    onSuccess: (saved) => {
      toast(saved ? 'Saved' : 'No changes to save', undefined, 'success');
    },
    onError: (e: any) => setVendorError(e.message),
  });

  // Vendor submit — one call, no windows. Flushes the deferred Service Details
  // draft first; a failed flush aborts the submit so the card never reaches
  // review without the details the operator typed. (The checklist is hidden
  // in vendor mode, so there is nothing of it to flush.)
  const submitVendor = useMutation({
    mutationFn: async () => {
      await serviceFormRef.current?.flush();
      const r = await api(`/fleet/job-cards/${id}/submit-vendor`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Vendor service submitted', 'Sent to the review queue for approval.', 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['service-history-checklist-answers', 'jobCard', id] });
    },
    onError: (e: any) => setVendorError(e.message),
  });

  const submitPast = useMutation({
    mutationFn: async (body: { windows: Array<{ startTime: string; endTime: string }> }) => {
      // Past mode is all-or-nothing: nothing reaches the server until the
      // tech clicks Submit. Flush both deferred editors (Service Details +
      // Checklist) before the windows post so the server-side meter guard
      // and the completeness check both see the values the tech just typed.
      try { await serviceFormRef.current?.flush(); } catch { /* surfaced below */ }
      try { await checklistRef.current?.flush(); } catch { /* surfaced below */ }
      const r = await api(`/fleet/job-cards/${id}/submit-past`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Past service submitted', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['time-logs', 'jobCard', id] });
      qc.invalidateQueries({ queryKey: ['service-history-checklist-answers', 'jobCard', id] });
    },
    onError: (e: any) => setPastError(e.message),
  });

  const workflow = useMutation({
    mutationFn: async (verb: 'start' | 'pause' | 'submit') => {
      const r = await api(`/fleet/job-cards/${id}/${verb}`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (_data, verb) => {
      toast(`Job card: ${verb}`, undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['time-logs', 'jobCard', id] });
      qc.invalidateQueries({ queryKey: ['service-history-checklist-answers', 'jobCard', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const approve = useMutation({
    mutationFn: async () => {
      // Vendor "Approve Now" fires from the deferred form — commit the draft
      // first or the approval lands on an empty service record. A failed
      // flush throws and aborts the approve.
      if (vendorFormVisible) await serviceFormRef.current?.flush();
      const r = await api(`/fleet/job-cards/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ reviewNotes: reviewNotesDraft || undefined }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Approved', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const reject = useMutation({
    mutationFn: async () => {
      const r = await api(`/fleet/job-cards/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reviewNotes: reviewNotesDraft || undefined }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Rejected', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  // Reverse-transition mutations. Both POST with no body and invalidate the
  // same queries the forward transitions invalidate so the page rerenders
  // with the new status (and the Review panel + revert buttons re-evaluate).
  const revertToCompleted = useMutation({
    mutationFn: async () => {
      const r = await api(`/fleet/job-cards/${id}/revert-to-completed`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Reverted to Completed', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['/servicing/history', shId] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const revertToActive = useMutation({
    mutationFn: async () => {
      const r = await api(`/fleet/job-cards/${id}/revert-to-active`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Reverted to Active', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/fleet/job-cards', id] });
      qc.invalidateQueries({ queryKey: ['/servicing/history', shId] });
      qc.invalidateQueries({ queryKey: ['time-logs', 'jobCard', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Derived inline-validation, one entry per row. `null` means the row is
  // either fully valid or still being filled in (no error to display).
  // Aggregated `pastAnyError` drives the Submit button's disabled state.
  // Lives above the early-return guard below so the hook runs on every
  // render in lockstep with `pastRows`.
  // NZ-date string (YYYY-MM-DD) — NOT toISOString().slice(0,10), which returns
  // the UTC date and rolls back a day for users east of UTC (NZ mornings would
  // block the actual 'today' from being selected). Host-local parts had the
  // same flaw offshore; toDateInputValue pins it to the depot's calendar.
  const todayLocalStr = toDateInputValue(new Date());
  // Per-row checks (date / order / future) — these render inline under
  // each row's End Time input.
  const pastRowErrors: Array<string | null> = React.useMemo(() => {
    if (!pastMode) return [];
    const today = todayLocalStr;
    return pastRows.map(({ date, start, end }) => {
      if (!date && !start && !end) return null;
      if (date && date > today) return 'Date cannot be in the future.';
      if (start && end && start >= end) return 'End time must be after start time.';
      if (date && end) {
        const endDT = new Date(`${date}T${end || '00:00'}`);
        if (Number.isFinite(endDT.getTime()) && endDT.getTime() > Date.now()) {
          return 'End time cannot be in the future.';
        }
      }
      return null;
    });
  }, [pastMode, pastRows, todayLocalStr]);
  // Pairwise overlap detection within this submission. Tracked as a set
  // of row indices (for the red border) plus a single shared message
  // shown once below all rows instead of duplicating the long sentence
  // on every offending row. Different technicians submit on separate
  // arrays, so cross-tech overlap is allowed by construction.
  const pastOverlapRows = React.useMemo<Set<number>>(() => {
    const out = new Set<number>();
    if (!pastMode) return out;
    const intervals = pastRows.map(({ date, start, end }, idx) => {
      if (pastRowErrors[idx]) return null;
      if (!date || !start || !end) return null;
      const s = new Date(`${date}T${start}`).getTime();
      const e = new Date(`${date}T${end}`).getTime();
      if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
      return { s, e };
    });
    for (let i = 0; i < intervals.length; i++) {
      const a = intervals[i];
      if (!a) continue;
      for (let j = i + 1; j < intervals.length; j++) {
        const b = intervals[j];
        if (!b) continue;
        // Half-open intervals [s, e): touching ends (10:00-10:10 + 10:10-10:20) is OK.
        if (a.s < b.e && b.s < a.e) {
          out.add(i);
          out.add(j);
        }
      }
    }
    return out;
  }, [pastMode, pastRows, pastRowErrors]);
  const pastHasOverlap = pastOverlapRows.size > 0;
  const pastAnyError = pastHasOverlap || pastRowErrors.some((e) => !!e);

  if (isLoading || !jc) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const status = jc.status ?? 'active';
  const openLog = timeLogs.find((l) => !l.endTime);
  const hasOpenSession = !!openLog;
  // Only the technicians assigned to the job card can drive the time clock —
  // a supervisor opening the same page sees the controls but they're disabled
  // (with a tooltip). Approvers / reviewers still use the Review block below.
  // `currentUserId` is hoisted to the top of the component so the hook
  // ordering stays valid through the early-return guards.
  const isAssignee = !!currentUserId && (jc.assignedTo ?? []).some((a) => a.id === currentUserId);
  // Multi-assignee submission state. Each tech submits independently;
  // the card only flips to 'completed' once every assignee has a row in
  // `submissions[]`. While the card is still 'active', a tech who has
  // already submitted should see their controls disabled — they're done
  // with their part. The visible progress badge below counts how many
  // of N have submitted so far.
  const submissions = jc.submissions ?? [];
  const submissionByUserId = new Map(submissions.map((s) => [
    typeof s.employee === 'string' ? s.employee : s.employee.id,
    s.submittedAt,
  ]));
  const mySubmissionAt = currentUserId ? submissionByUserId.get(currentUserId) : undefined;
  const hasSubmittedMine = !!mySubmissionAt;
  const totalAssigned = (jc.assignedTo ?? []).length;
  const submittedCount = (jc.assignedTo ?? []).filter((a) => submissionByUserId.has(a.id)).length;
  // 4-status model. The fine-grained start/pause/submit availability now depends
  // on whether the caller currently has an open TimeLog rather than on the JobCard state.
  // Once *this* tech has submitted, their controls disable even though the
  // card is still 'active' for the remaining assignees.
  // Live Start/Pause are suppressed in past mode — the past-service form
  // submits a closed TimeLog atomically with the JC submission instead.
  // The live Submit button (canSubmit) is also hidden in past mode; the
  // past panel owns the only Submit there.
  // Vendor mode suppresses the live controls for the same reason past mode
  // does: its own panel owns the only Submit, and there is no session to
  // start or pause.
  const canStart  = !deferredMode && status === 'active' && !hasOpenSession && !hasSubmittedMine;
  const canPause  = !deferredMode && status === 'active' && hasOpenSession  && !hasSubmittedMine;
  const canSubmit = !deferredMode && status === 'active' && !hasSubmittedMine;
  const canReview = status === 'completed' && granted.has('job-card.approve');
  // Reverse-transition gates — surfaced as outline buttons in the Review
  // panel alongside Approve/Reject. Visibility mirrors the rowAction
  // predicates in `configs.ts` so the two entry points stay aligned.
  const canRevertToCompleted = (status === 'approved' || status === 'voided')
    && granted.has('job-card.revert-to-completed');
  const canRevertToActive = status === 'completed' && granted.has('job-card.revert-to-active');
  const isLocked  = status === 'approved' || status === 'voided';
  // Two-tier post-completion edit grants. The override key implies the
  // plain edit key (a role with override doesn't also need the basic
  // grant). `editOverride` flips off the meter validations; `editService`
  // alone keeps them on.
  const hasEditService = granted.has('job-card.edit-service') || granted.has('job-card.edit-service-override');
  const editOverride   = granted.has('job-card.edit-service-override');
  // "View mode" — the page is past the technician's data-entry window.
  // In all three states the form lands read-only by default; a role
  // with either edit grant can flip into Edit on a completed card.
  // (isLocked covers approved / voided, which stay read-only.)
  const isViewMode = status === 'completed' || isLocked;
  const canToggleEdit = status === 'completed' && hasEditService;
  // Final read-only signal passed to every editable section. The existing
  // role-based locks still hold; on top of those:
  //  - completed cards are read-only until Edit is clicked,
  //  - an already-submitted tech sees the form locked even while the card
  //    is still 'active' waiting on other assignees (their part is done),
  //  - on an 'active' card in the LIVE (non-past) flow, editing requires
  //    an open time-tracking session — pausing closes the session, so the
  //    fields lock until Resume re-opens one. Past and vendor modes skip
  //    this gate: neither opens a session before the single Submit, and in
  //    vendor mode none ever exists, so without the exemption the meter
  //    field would be permanently read-only.
  const formReadOnly = isLocked
    || (status === 'completed' && !hasEditService)
    || (isViewMode && !isEditing)
    || (status === 'active' && hasSubmittedMine)
    || (status === 'active' && !deferredMode && !hasOpenSession);
  const totalHoursLive = timeLogs.reduce((s, l) => s + (l.hours ?? 0), 0);

  // Past-mode Submit handler. Walks every row in `pastRows`, runs the
  // same checks the live error banner does, then POSTs the assembled
  // windows array. Defence-in-depth: even with the disabled-button
  // guard stale, the POST still won't go through with invalid data.
  async function handlePastSubmit() {
    if (pastRows.length === 0) {
      setPastError('Add at least one time window.');
      return;
    }
    const parsed: Array<{ startTime: string; endTime: string }> = [];
    for (let i = 0; i < pastRows.length; i++) {
      const row = pastRows[i];
      const label = pastRows.length > 1 ? `Row ${i + 1}: ` : '';
      if (!row.date || !row.start || !row.end) {
        setPastError(`${label}Date, start time and end time are all required.`);
        return;
      }
      const startISO = new Date(`${row.date}T${row.start}`);
      const endISO   = new Date(`${row.date}T${row.end}`);
      if (!Number.isFinite(startISO.getTime()) || !Number.isFinite(endISO.getTime())) {
        setPastError(`${label}Could not parse the date or time values.`);
        return;
      }
      if (startISO.getTime() >= endISO.getTime()) {
        setPastError(`${label}Start time must be before end time.`);
        return;
      }
      if (endISO.getTime() > Date.now()) {
        setPastError(`${label}End time cannot be in the future.`);
        return;
      }
      parsed.push({ startTime: startISO.toISOString(), endTime: endISO.toISOString() });
    }
    // Pairwise overlap re-check on the parsed ISO list — mirrors the
    // live `pastRowErrors` gate so we still catch the case where two
    // rows would clash even after the live message is masked by an
    // edit elsewhere.
    for (let i = 0; i < parsed.length; i++) {
      const aS = new Date(parsed[i].startTime).getTime();
      const aE = new Date(parsed[i].endTime).getTime();
      for (let j = i + 1; j < parsed.length; j++) {
        const bS = new Date(parsed[j].startTime).getTime();
        const bE = new Date(parsed[j].endTime).getTime();
        if (aS < bE && bS < aE) {
          setPastError('Two of your time windows overlap. Please adjust the times so they don\'t cover the same period.');
          return;
        }
      }
    }
    if (!isAssignee) {
      setPastError('Only the assigned technician can log a past service for this job card.');
      return;
    }
    // Block submit while the checklist still has unanswered rows. The live
    // flow has Start/Pause/Submit as separate stages, so the tech naturally
    // walks the checklist between Start and Submit; the past flow collapses
    // those, so we surface the same expectation up-front as a hard gate.
    if (checklistUnanswered > 0) {
      setPastError(
        `Please answer all checklist items before submitting (${checklistUnanswered} unanswered).`,
      );
      return;
    }
    if (!(await confirm({ title: 'Submit this past service?', confirmLabel: 'Submit' }))) return;
    setPastError(null);
    submitPast.mutate({ windows: parsed });
  }
  // Once the tech has submitted in past mode, collapse back to the
  // standard view-mode rendering — same shape the "View Service"
  // rowAction lands on. The Past Service Window form would just show
  // stale empty inputs; the Sessions list + read-only Service Details /
  // Checklist / Documents tell the right story.
  const pastFormVisible = pastMode && !hasSubmittedMine;
  const showPastSubmit = pastFormVisible && status === 'active';
  // Past-mode editing readiness — true once the tech is allowed to fill
  // in the window AND the server has finished initialising the linked
  // ServiceHistory. Drives BOTH the input disabled state AND the visibility
  // of Service Details / Checklist / Documents below: as soon as the
  // date/time inputs become editable, the other sections also flip from
  // their "preparing…" placeholder to the real editors (mirrors the
  // live flow where clicking Start unlocks everything at once).
  const pastReady = pastFormVisible && !!shId && isAssignee && status === 'active';
  // Transient disabled (mutations in flight). Kept separate from `pastReady`
  // so the editor sections stay visible during a pending submit instead
  // of flickering back to the placeholder.
  const pastInputsDisabled = !pastReady || submitPast.isPending || initPast.isPending;
  // Normal mode keeps the original "click Start to reveal the editors"
  // behaviour: even if a ServiceHistory already exists (e.g., this card
  // was previously opened in past mode, which pre-creates SH via
  // /init-past), we still gate Service Details / Checklist / Documents
  // on at least one TimeLog or a stamped startDate, so a brand-new
  // normal-flow session looks the same as it always did.
  // Past mode unconditionally reveals these — that's the whole point of
  // skipping Start in the back-fill flow.
  // Vendor cards never get a TimeLog or a startDate (/submit-vendor stamps
  // neither), so without the servicedWith exemption a submitted vendor card
  // opened via "View Service" showed "Start the service…" placeholders in
  // place of its Service Details and Documents.
  const liveServiceReady = !!shId
    && (timeLogs.length > 0 || !!jc.startDate || jc.servicedWith === 'Vendor');
  // Past-mode pre-submit: show editors only once `pastReady` flips true
  // (mirrors how Start unlocks them in the live flow). Past-mode
  // post-submit: collapse back to live-mode readiness so the page reads
  // like "View Service" — the user sees what they submitted instead of
  // a placeholder.
  // Vendor mode mirrors past mode's "reveal the editors once the record
  // exists" behaviour, minus the assignee requirement — a vendor job has no
  // assignee by definition, so gating on `isAssignee` (as `pastReady` does)
  // would leave the form permanently hidden.
  const vendorFormVisible = vendorMode && status === 'active';
  const vendorReady = vendorFormVisible && !!shId;
  const vendorInputsDisabled = !vendorReady || submitVendor.isPending || initPast.isPending;
  const showServiceForm = pastFormVisible ? pastReady
    : vendorMode ? (vendorFormVisible ? vendorReady : liveServiceReady)
    : liveServiceReady;

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backTo}><ChevronLeft className="h-4 w-4" /> {backLabel}</Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-2xl">{jc.jobSheetNumber}</CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                {jc.asset?.fleetNumber || '—'}
                {jc.servicePlanSchedule?.name && <span> · {jc.servicePlanSchedule.name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_BADGE[status] ?? 'default'} className="text-sm">{jobCardStatusLabel(status)}</Badge>
              {/* Download PDF — mirrors what the View Service page shows
                  (overview + time logs + service details + checklist + review
                  notes; Documents & Photos intentionally excluded). Fetches
                  the full checklist rows on demand so the page itself doesn't
                  load them when not needed. Hidden when the service hasn't
                  started yet — there's no ServiceHistory / time logs / checklist
                  state to render, so the PDF would just be an empty shell. */}
              {liveServiceReady && (
              <Button
                size="sm"
                variant="outline"
                disabled={pdfBusy}
                onClick={async () => {
                  try {
                    setPdfBusy(true);
                    const answers = shId
                      ? (await api<Array<{ task: string | null; answer: string | null; comments: string | null; sortOrder?: number | null }>>(
                          `/service-history-checklist-answers?jobCard=${encodeURIComponent(id)}&limit=500&sort=sortOrder&order=asc`,
                        )).data ?? []
                      : [];
                    downloadJobCardServicePdf({
                      jc: jc as any,
                      sh: sh ?? null,
                      timeLogs,
                      checklistAnswers: answers.map((a) => ({
                        task: a.task, answer: a.answer, comments: a.comments,
                      })),
                      statusLabel: jobCardStatusLabel(status),
                    });
                  } catch (e: any) {
                    toast('Could not generate PDF', e?.message, 'error');
                  } finally {
                    setPdfBusy(false);
                  }
                }}
              >
                <Download className="h-4 w-4 mr-1" /> {pdfBusy ? 'Preparing…' : 'Download PDF'}
              </Button>
              )}
              {/* Edit / Save / Cancel — only meaningful on completed cards
                  where the reviewer still has edit rights. Approved /
                  voided cards stay read-only with no affordance here. */}
              {canToggleEdit && !isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
              )}
              {canToggleEdit && isEditing && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Drop any in-memory edits by re-syncing the editable
                      // sections from the server, then leave edit mode.
                      serviceFormRef.current?.reset();
                      qc.invalidateQueries({ queryKey: ['/servicing/history', shId] });
                      qc.invalidateQueries({ queryKey: ['service-history-checklist-answers', 'jobCard', id] });
                      setIsEditing(false);
                    }}
                    disabled={updateServiceHistory.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      // Pre-flight: when the role only carries the plain
                      // edit grant (no override), enforce the same meter
                      // guard the tech-flow uses. Override-grant roles
                      // already get `skipMeterValidation`, so validate()
                      // returns null for them.
                      const err = serviceFormRef.current?.validate() ?? null;
                      if (err) {
                        toast('Cannot save', err, 'error');
                        return;
                      }
                      try {
                        const saved = await serviceFormRef.current?.flush();
                        if (saved) toast('Service details saved', undefined, 'success');
                        setIsEditing(false);
                      } catch {
                        /* surfaced via the mutation's onError toast */
                      }
                    }}
                    disabled={updateServiceHistory.isPending}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Service Plan</Label>
              <div>{jc.servicePlan?.name || '—'}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Assigned To</Label>
              <div>{(jc.assignedTo ?? []).map((a) => a.fullName).filter(Boolean).join(', ') || '—'}</div>
            </div>
          </div>
          {jc.workDescription && (
            <div>
              <Label className="text-xs text-muted-foreground">Work Description</Label>
              <div className="whitespace-pre-wrap">{jc.workDescription}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- Time tracking + workflow controls ----
           Live mode: Total / Open session + Start / Pause / Submit.
           Past mode: Date / Start Time / End Time inputs replace the
           Start/Pause buttons; the SAME Submit button on the right is
           the only one on the page and posts to /submit-past with the
           validated window. */}
      {/* Vendor mode replaces the whole time-tracking card. There is no Atlas
          labour to time, so showing Total / Open session / Start / Pause would
          be inviting the operator to record something meaningless. */}
      {vendorFormVisible ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" /> Vendor Service
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {jc.vendor?.name
                ? <>Work performed by <span className="font-medium text-foreground">{jc.vendor.name}</span>. </>
                : 'Work performed by an external vendor. '}
              No Atlas labour, meter reading or checklist is required. Add any
              details you want on record below and Save, then send it for
              approval — or approve it now if you have the permission.
            </p>
            {vendorError && (
              <p className="text-sm text-destructive">{vendorError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setVendorError(null); saveVendor.mutate(); }}
                disabled={vendorInputsDisabled || saveVendor.isPending || approve.isPending}
              >
                {saveVendor.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setVendorError(null); submitVendor.mutate(); }}
                disabled={vendorInputsDisabled || saveVendor.isPending || approve.isPending}
              >
                {submitVendor.isPending ? 'Sending…' : 'Send for Approval'}
              </Button>
              {/* Straight to approved, skipping the queue. Offered only to a
                  holder of job-card.approve; the server enforces the same. */}
              {granted.has('job-card.approve') && (
                <Button
                  onClick={() => { setVendorError(null); approve.mutate(); }}
                  disabled={approve.isPending || submitVendor.isPending || initPast.isPending}
                >
                  {approve.isPending ? 'Approving…' : 'Approve Now'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" /> {pastFormVisible ? 'Past Service Window' : 'Time Tracking'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pastFormVisible ? (
            <div className="space-y-3">
              {/* Multi-assignee progress is rendered above the rows in past
                  mode to keep the row layout uniform per entry. */}
              {totalAssigned > 1 && (
                <div className="text-sm">
                  <div className="text-xs text-muted-foreground">Submitted</div>
                  <div className="text-lg font-semibold">{submittedCount} / {totalAssigned}</div>
                </div>
              )}
              {pastRows.map((row, idx) => {
                const rowError = pastRowErrors[idx] ?? null;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-end gap-2 flex-wrap">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1 min-w-[280px]">
                        <div>
                          {idx === 0 && <Label className="text-xs">Date<span className="text-destructive"> *</span></Label>}
                          <Input
                            type="date"
                            value={row.date}
                            max={todayLocalStr}
                            disabled={pastInputsDisabled}
                            onChange={(e) => updatePastRow(idx, { date: e.target.value })}
                          />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-xs">Start Time<span className="text-destructive"> *</span></Label>}
                          <NzTimeInput
                            ariaLabel="Start"
                            value={row.start}
                            disabled={pastInputsDisabled}
                            // 24-hour locale-immune picker; server still re-
                            // validates via the shared zod schema.
                            max={row.end || undefined}
                            onChange={(v) => updatePastRow(idx, { start: v })}
                          />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-xs">End Time<span className="text-destructive"> *</span></Label>}
                          <NzTimeInput
                            ariaLabel="End"
                            value={row.end}
                            disabled={pastInputsDisabled}
                            min={row.start || undefined}
                            onChange={(v) => updatePastRow(idx, { end: v })}
                            invalid={!!rowError || pastOverlapRows.has(idx)}
                          />
                          {rowError && (
                            <p className="text-[11px] text-destructive mt-0.5">{rowError}</p>
                          )}
                        </div>
                      </div>
                      {pastRows.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Remove this time window"
                          title="Remove this time window"
                          disabled={pastInputsDisabled}
                          onClick={() => removePastRow(idx)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {pastHasOverlap && (
                <p className="text-xs text-destructive">
                  Two or more of your time windows overlap. Please adjust them so each one covers a different period.
                </p>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addPastRow}
                  disabled={pastInputsDisabled}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add another time window
                </Button>
                {showPastSubmit && (
                  <Button
                    onClick={handlePastSubmit}
                    disabled={submitPast.isPending || initPast.isPending || !isAssignee || !shId || pastAnyError}
                    title={!isAssignee
                      ? 'Only the assigned technician can submit a past service for this job card'
                      : !shId ? 'Preparing service record…'
                      : pastAnyError ? 'Fix the highlighted time window(s) before submitting.' : undefined}
                  >
                    <Send className="h-4 w-4 mr-1" /> Submit
                  </Button>
                )}
              </div>
            </div>
          ) : (
          <div className="flex items-end gap-3 flex-wrap">
            <>
              <div className="text-sm">
                <div className="text-xs text-muted-foreground">Total logged</div>
                <div className="text-lg font-semibold">{totalHoursLive.toFixed(2)} h</div>
              </div>
              <div className="text-sm">
                <div className="text-xs text-muted-foreground">Open session</div>
                <div className="text-lg font-semibold">
                  {openLog ? `Started ${new Date(openLog.startTime!).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}` : '—'}
                </div>
              </div>
              {/* Multi-assignee progress — only meaningful when more than
                  one tech is on the job. A single-tech card collapses
                  this column so the layout stays uncluttered. */}
              {totalAssigned > 1 && (
                <div className="text-sm">
                  <div className="text-xs text-muted-foreground">Submitted</div>
                  <div className="text-lg font-semibold">{submittedCount} / {totalAssigned}</div>
                </div>
              )}
              <div className="flex-1" />
            </>
            {/* Workflow buttons render for every viewer of this page so the
                report's Edit shortcut always lands on the same UI. They're
                only ENABLED for the technicians named in `assignedTo` who
                haven't yet submitted — a supervisor or already-submitted
                tech sees them greyed out with a tooltip explaining why. */}
            {canStart && (
              <Button
                onClick={() => workflow.mutate('start')}
                disabled={workflow.isPending || !isAssignee}
                title={!isAssignee ? 'Only the assigned technician can start time on this job card' : undefined}
              >
                <Play className="h-4 w-4 mr-1" /> {timeLogs.length > 0 ? 'Resume' : 'Start'}
              </Button>
            )}
            {canPause && (
              <Button
                variant="outline"
                onClick={() => workflow.mutate('pause')}
                disabled={workflow.isPending || !isAssignee}
                title={!isAssignee ? 'Only the assigned technician can pause time on this job card' : undefined}
              >
                <Pause className="h-4 w-4 mr-1" /> Pause
              </Button>
            )}
            {canSubmit && (
              <Button
                onClick={async () => {
                  // Flush any draft Service Details (odometer / engine hours /
                  // hubo / etc.) before submitting. A tech who types a value
                  // and taps Submit without the input losing focus would
                  // otherwise lose the unsaved value when the card flips to
                  // 'completed' and the form goes readOnly.
                  try { await serviceFormRef.current?.flush(); } catch { /* surfaced via mutation onError toast */ }
                  if (await confirm({ title: 'Submit this job card for review?', confirmLabel: 'Submit' })) workflow.mutate('submit');
                }}
                disabled={workflow.isPending || !isAssignee || updateServiceHistory.isPending}
                title={!isAssignee ? 'Only the assigned technician can submit this job card' : undefined}
              >
                <Send className="h-4 w-4 mr-1" /> Submit
              </Button>
            )}
          </div>
          )}
          {pastFormVisible && pastError && !pastAnyError && (
            <p className="text-xs text-destructive">{pastError}</p>
          )}
          {/* "You've submitted" notice — appears once this tech has
              recorded their submission. Card may still be 'active' if
              other assignees haven't submitted yet, so the buttons
              above stay hidden via `canStart/Pause/Submit` flags. */}
          {hasSubmittedMine && status === 'active' && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
              <span className="font-semibold">You submitted</span> at {fmtDateTime(mySubmissionAt)}.
              {totalAssigned > 1 && submittedCount < totalAssigned && (
                <span className="ml-1">Waiting on {totalAssigned - submittedCount} other technician(s) before this job card is marked completed.</span>
              )}
            </div>
          )}

          {!pastFormVisible && timeLogs.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">Sessions ({timeLogs.length})</summary>
              <div className="mt-2 space-y-1">
                {timeLogs.map((l) => (
                  <div key={l.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-xs border-b pb-1">
                    <span>{l.employee?.fullName || '—'}</span>
                    <span>{fmtDateTime(l.startTime)}</span>
                    <span>{l.endTime ? fmtDateTime(l.endTime) : <em>open</em>}</span>
                    <span>{l.hours != null ? `${l.hours} h` : ''}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </CardContent>
      </Card>
      )}

      {/* ---- Service Details (linked ServiceHistory record) ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4" /> Service Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!showServiceForm ? (
            <div className="text-sm text-muted-foreground py-2">
              {pastMode
                ? (!isAssignee
                    ? 'Only the assigned technician can log a past service for this job card.'
                    : 'Preparing the service record…')
                : vendorMode
                ? 'Preparing the service record…'
                : 'Start the service to create the linked service record.'}
            </div>
          ) : !sh ? (
            <div className="text-sm text-muted-foreground py-2">Loading…</div>
          ) : (
            <ServiceDetailsForm
              ref={serviceFormRef}
              sh={sh}
              readOnly={formReadOnly}
              // Defer per-field on-blur saves in two cases:
              //  - view-mode editing (so Cancel can discard the draft), and
              //  - past-service entry (the entire past flow is all-or-nothing:
              //    nothing reaches the server until the single Submit fires).
              // Active live-flow sessions keep the original autosave.
              deferCommit={(isViewMode && isEditing) || pastFormVisible || vendorFormVisible}
              // Returns the mutation promise so the form's `flush()` can
              // await the pending PUT before the parent fires /submit.
              onSave={(patch) => updateServiceHistory.mutateAsync(patch)}
              requiredMeter={requiredMeter}
              priorReading={priorReading}
              trackUsageAs={trackUsageAs}
              // When the role carries the override grant AND the user is
              // currently in view-mode edit, drop the meter validations
              // entirely so back-fill corrections (e.g. fixing a wrong
              // odometer reading) aren't blocked.
              skipMeterValidation={(isViewMode && isEditing && editOverride) || vendorFormVisible}
            />
          )}
        </CardContent>
      </Card>

      {/* ---- Service checklist ----
           Hidden for vendor jobs: the checklist is our technicians' inspection
           sheet, and a vendor doesn't fill it in. /submit-vendor doesn't seed
           the answers either, so an empty sheet here would only read as
           outstanding work. */}
      {!vendorFormVisible && jc.servicedWith !== 'Vendor' && (
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Service Checklist</CardTitle></CardHeader>
        <CardContent>
          {!showServiceForm ? (
            <div className="text-sm text-muted-foreground py-2">
              {pastMode
                ? (!isAssignee
                    ? 'Only the assigned technician can log a past service for this job card.'
                    : 'Preparing the checklist…')
                : vendorMode ? 'Preparing…'
                : 'Start the service to load the checklist.'}
            </div>
          ) : (
            <JobCardChecklist
              ref={checklistRef}
              jobCardId={id}
              readOnly={formReadOnly}
              // Past mode buffers edits locally and only PUTs them on Submit
              // (via the imperative handle's flush()), to match the all-or-
              // nothing semantics of the surrounding past-service form.
              deferCommit={pastFormVisible || vendorFormVisible}
              emptyMessage={pastMode
                ? "No checklist items configured for this job card's Service Plan Schedule."
                : undefined}
            />
          )}
        </CardContent>
      </Card>
      )}

      {/* ---- Documents & Photos ---- */}
      {/* Mirrors the same /service-history-documents collection the Service
          History detail view shows, so techs can upload during the job and
          the records appear on the downstream service record automatically. */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Documents & Photos</CardTitle></CardHeader>
        <CardContent>
          {!showServiceForm ? (
            <div className="text-sm text-muted-foreground py-2">
              {pastMode
                ? (!isAssignee
                    ? 'Only the assigned technician can log a past service for this job card.'
                    : 'Preparing the service record…')
                : vendorMode ? 'Preparing…'
                : 'Start the service to enable uploads.'}
            </div>
          ) : (
            <JobCardDocuments serviceHistoryId={shId!} readOnly={formReadOnly} />
          )}
        </CardContent>
      </Card>

      {/* ---- Reviewer panel ---- */}
      {(status === 'completed' || status === 'approved' || status === 'voided') && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Review</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea
                value={reviewNotesDraft}
                onChange={(e) => setReviewNotesDraft(e.target.value)}
                disabled={!canReview}
                placeholder="Optional review notes…"
              />
            </div>
            {(canReview || canRevertToCompleted || canRevertToActive) && (
              <div className="flex gap-2 justify-end flex-wrap">
                {canRevertToActive && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (await confirm({
                        title: 'Revert this job card to Active?',
                        description: 'All submissions will be cleared and technicians can re-submit.',
                        destructive: true,
                        confirmLabel: 'Revert to Active',
                      })) revertToActive.mutate();
                    }}
                    disabled={revertToActive.isPending}
                  >Revert to Active</Button>
                )}
                {canRevertToCompleted && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (await confirm({
                        title: 'Revert this job card to Completed?',
                        description: 'The current review decision will be cleared so it can be re-reviewed.',
                        destructive: true,
                        confirmLabel: 'Revert to Completed',
                      })) revertToCompleted.mutate();
                    }}
                    disabled={revertToCompleted.isPending}
                  >Revert to Completed</Button>
                )}
                {canReview && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={async () => { if (await confirm({ title: 'Reject this job card?', description: 'This will void it.', destructive: true, confirmLabel: 'Reject' })) reject.mutate(); }}
                      disabled={reject.isPending}
                    >Reject</Button>
                    <Button onClick={() => approve.mutate()} disabled={approve.isPending}>Approve</Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export interface ServiceDetailsFormHandle {
  /** Persist any draft values that diverge from the server-known `sh` and
   *  resolve once the save settles. Called by the parent's Submit handler
   *  so a tech who types Engine Hours and immediately taps Submit (without
   *  the input losing focus first) doesn't lose the unsaved value.
   *  Resolves with `true` when at least one field was saved, `false` when
   *  the draft matched the server state (nothing to do). */
  flush: () => Promise<boolean>;
  /** Discard in-memory draft edits and snap the form back to the
   *  server-known `sh`. Used by Cancel in view-mode edit. */
  reset: () => void;
  /** Pre-flight check against the current draft. Returns `null` when OK,
   *  or a user-facing message when the meter validation would fail. */
  validate: () => string | null;
}

const ServiceDetailsForm = React.forwardRef<ServiceDetailsFormHandle, {
  sh: ServiceHistory;
  readOnly: boolean;
  onSave: (patch: Partial<ServiceHistory>) => Promise<unknown>;
  /** Which meter the asset is tracked by — surfaces the inline "required"
   *  + "must be ≥ prior reading" hints under the matching input. Null when
   *  the asset hasn't picked a tracking mode (nothing to validate). */
  requiredMeter?: 'odometer' | 'hubo' | 'engineHours' | null;
  /** Latest prior reading on the same asset for the required meter — the
   *  Submit guard rejects when the technician's draft is below this. */
  priorReading?: number | null;
  /** Human label for the meter ("Odometer" / "Hubometer" / "Hours") — used
   *  verbatim in the hint copy. */
  trackUsageAs?: string | null;
  /** When true, suppress per-field on-blur autosave. The form still tracks
   *  draft edits locally; the parent commits them by calling `flush()` on
   *  the imperative handle (or discards via `reset()`). */
  deferCommit?: boolean;
  /** When true, suppress the inline "must be ≥ prior reading" / "required"
   *  hints. Set by the parent for roles with the override grant editing a
   *  completed card. The server still applies whatever guard it chooses. */
  skipMeterValidation?: boolean;
}>(function ServiceDetailsForm({ sh, readOnly, onSave, requiredMeter, priorReading, trackUsageAs, deferCommit = false, skipMeterValidation = false }, ref) {
  const [draft, setDraft] = React.useState(sh);
  // Only resync the draft when the underlying ServiceHistory record changes
  // (i.e. the user navigated to a different SH). Re-syncing on every scalar
  // field would wipe in-progress edits in OTHER fields whenever one field's
  // blur-save lands: the post-save refetch returns a new `sh` where the
  // just-saved field differs, the effect fires, `setDraft(sh)` replaces the
  // entire draft, and characters the user has typed in another field since
  // the blur are silently overwritten by the (still-stale) server value.
  React.useEffect(() => { setDraft(sh); }, [sh.id]);

  // Keep the latest draft / sh in a ref so `flush()` always reads the
  // current values — without this, the imperative handle would close over
  // the draft from its first render and miss subsequent edits.
  const draftRef = React.useRef(draft);
  const shRef = React.useRef(sh);
  React.useEffect(() => { draftRef.current = draft; }, [draft]);
  React.useEffect(() => { shRef.current = sh; }, [sh]);

  React.useImperativeHandle(ref, () => ({
    async flush() {
      const d = draftRef.current as any;
      const s = shRef.current as any;
      const patch: Record<string, unknown> = {};
      for (const field of ['odometer', 'engineHours', 'hubo', 'descriptionOfWork', 'faultDescriptions'] as const) {
        if (d[field] !== s[field]) patch[field] = d[field];
      }
      if (Object.keys(patch).length === 0) return false;
      await onSave(patch);
      return true;
    },
    reset() {
      setDraft(shRef.current);
    },
    validate() {
      if (skipMeterValidation) return null;
      if (!requiredMeter) return null;
      const v = (draftRef.current as any)[requiredMeter];
      const label = trackUsageAs ?? requiredMeter;
      if (v == null || v === '') return `${label} is required.`;
      if (priorReading != null && Number(v) < priorReading) {
        return `${label} must be ≥ ${priorReading} (previous reading on this asset).`;
      }
      return null;
    },
  }), [onSave, skipMeterValidation, requiredMeter, priorReading, trackUsageAs]);

  function commit(field: keyof ServiceHistory, value: unknown) {
    // Defer mode: parent owns the save lifecycle via flush() — skip the
    // per-field PUT so Cancel can truly discard the draft.
    if (deferCommit) return;
    if ((draft as any)[field] === (sh as any)[field]) return;
    void onSave({ [field]: value } as Partial<ServiceHistory>);
  }

  function num(v: string): number | null {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  // Pre-flight hint that mirrors the /submit server guard. Returns:
  //   - 'missing' when the asset is tracked by this meter and the draft is blank,
  //   - 'backwards' when the draft is less than the prior reading on the asset,
  //   - 'ok'  when no issue (or this meter isn't the required one).
  function meterIssue(field: 'odometer' | 'hubo' | 'engineHours'):
    'missing' | 'backwards' | 'ok' {
    // No editing → no inline guard. The hint is a pre-flight for the tech
    // about to save; in read-only view it just looks like a broken record.
    if (readOnly) return 'ok';
    if (skipMeterValidation) return 'ok';
    if (requiredMeter !== field) return 'ok';
    const v = (draft as any)[field];
    if (v == null || v === '') return 'missing';
    if (priorReading != null && Number(v) < priorReading) return 'backwards';
    return 'ok';
  }
  function hint(issue: 'missing' | 'backwards' | 'ok'): React.ReactNode {
    if (issue === 'ok') return null;
    const msg = issue === 'missing'
      ? `Required — asset is tracked by ${trackUsageAs ?? 'this meter'}.`
      : `Must be ≥ ${priorReading} (previous reading on this asset).`;
    return <p className="text-[11px] text-destructive mt-0.5">{msg}</p>;
  }
  const odoIssue   = meterIssue('odometer');
  const hoursIssue = meterIssue('engineHours');
  const huboIssue  = meterIssue('hubo');
  const invalidClass = 'border-destructive focus-visible:ring-destructive';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Odometer{requiredMeter === 'odometer' && <span className="text-destructive"> *</span>}</Label>
          <Input
            type="number"
            value={draft.odometer ?? ''}
            disabled={readOnly}
            onChange={(e) => setDraft({ ...draft, odometer: num(e.target.value) })}
            onBlur={() => commit('odometer', draft.odometer)}
            className={odoIssue !== 'ok' ? invalidClass : undefined}
          />
          {hint(odoIssue)}
        </div>
        <div>
          <Label className="text-xs">Engine Hours{requiredMeter === 'engineHours' && <span className="text-destructive"> *</span>}</Label>
          <Input
            type="number"
            value={draft.engineHours ?? ''}
            disabled={readOnly}
            onChange={(e) => setDraft({ ...draft, engineHours: num(e.target.value) })}
            onBlur={() => commit('engineHours', draft.engineHours)}
            className={hoursIssue !== 'ok' ? invalidClass : undefined}
          />
          {hint(hoursIssue)}
        </div>
        <div>
          <Label className="text-xs">Hubo{requiredMeter === 'hubo' && <span className="text-destructive"> *</span>}</Label>
          <Input
            type="number"
            value={draft.hubo ?? ''}
            disabled={readOnly}
            onChange={(e) => setDraft({ ...draft, hubo: num(e.target.value) })}
            onBlur={() => commit('hubo', draft.hubo)}
            className={huboIssue !== 'ok' ? invalidClass : undefined}
          />
          {hint(huboIssue)}
        </div>
      </div>
      <div>
        <Label className="text-xs">Description of Work</Label>
        <Textarea
          value={draft.descriptionOfWork ?? ''}
          disabled={readOnly}
          onChange={(e) => setDraft({ ...draft, descriptionOfWork: e.target.value || null })}
          onBlur={() => commit('descriptionOfWork', draft.descriptionOfWork)}
          rows={3}
        />
      </div>
      <div>
        <Label className="text-xs">Fault Descriptions</Label>
        <Textarea
          value={draft.faultDescriptions ?? ''}
          disabled={readOnly}
          onChange={(e) => setDraft({ ...draft, faultDescriptions: e.target.value || null })}
          onBlur={() => commit('faultDescriptions', draft.faultDescriptions)}
          rows={2}
        />
      </div>
    </div>
  );
});
